/**
 * Ballot status — which records the source itself says are on the ballot.
 *
 * Democracy Works and Ballotpedia stamp a `status` on the measures and
 * candidates they return, and a record can stay in a feed long after it leaves
 * the ballot: California's 2026 feed carried six withdrawn initiatives
 * (`status: "notOnBallot"`) alongside the fourteen certified propositions, and
 * the Ballot Preview listed and numbered all twenty.
 *
 * The backend drops these before they reach a client
 * (`votemate-backend/utils/ballot_status.py`); this mirrors that deny-list so a
 * payload from an older backend — a partner's cached embed can run against one
 * — renders and counts the same. Vocabulary is a **deny-list**: a record is off
 * the ballot only when its status plainly says so. A missing status, or a value
 * this module has never seen, keeps the record.
 *
 * Observed values (2026-09): Democracy Works measures `onBallot` /
 * `notOnBallot`; Democracy Works candidates `onBallot` / `declared` /
 * `advanced` / `withdrewStillOnBallot`; Ballotpedia geo measures
 * `"On the ballot"`; Ballotpedia geo candidates `"Candidacy Declared"`.
 *
 * `votemate-mobile-app/src/utils/ballotStatus.ts` is the same module; keep the
 * two in step.
 */

/** Statuses meaning the record does not appear on the ballot, normalized
 *  (lowercase, alphanumerics only) so `notOnBallot`, `Not on the ballot` and
 *  `not_on_ballot` all read the same. Keep identical to the backend set. */
export const OFF_BALLOT_STATUSES: ReadonlySet<string> = new Set([
  'notonballot',
  'notontheballot',
  'withdrawn',
  'withdrew',
  'removed',
  'removedfromballot',
  'cancelled',
  'canceled',
  'disqualified',
  'failedtoqualify',
  'didnotqualify',
  'blocked',
  'struck',
  'struckfromballot',
]);

/** Lowercase alphanumeric form of a status value; `''` when absent. */
export const normalizeStatus = (status: unknown): string =>
  status === null || status === undefined
    ? ''
    : String(status)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '');

const statusOnBallot = (status: unknown): boolean => {
  const normalized = normalizeStatus(status);
  return !normalized || !OFF_BALLOT_STATUSES.has(normalized);
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** True unless the measure's `status` says it left the ballot. */
export const isMeasureOnBallot = (measure: unknown): boolean =>
  !isRecord(measure) || statusOnBallot(measure.status);

/** True unless the candidate's status says they left the ballot. Democracy
 *  Works spells the field `status`; Ballotpedia's geo records use `cand_status`
 *  / `candidate_status`. A plain-string candidate has no status and stays. */
export const isCandidateOnBallot = (candidate: unknown): boolean =>
  !isRecord(candidate) ||
  statusOnBallot(candidate.status || candidate.cand_status || candidate.candidate_status);

/** True unless Democracy Works marks the contest `cancelled`. */
export const isContestOnBallot = (contest: unknown): boolean =>
  !isRecord(contest) || !contest.cancelled;

export const filterOnBallotMeasures = <M>(measures: readonly M[] | null | undefined): M[] =>
  (measures ?? []).filter(isMeasureOnBallot);

export const filterOnBallotCandidates = <C>(candidates: readonly C[] | null | undefined): C[] =>
  (candidates ?? []).filter(isCandidateOnBallot);

/** Drops cancelled contests. A kept contest's roster is a separate step —
 *  `withOnBallotCandidates` — so this stays the exact mirror of the backend. */
export const filterOnBallotContests = <C>(contests: readonly C[] | null | undefined): C[] =>
  (contests ?? []).filter(isContestOnBallot);

/** The contest with candidates who left the race removed from its roster. */
export const withOnBallotCandidates = <C extends { candidates?: unknown }>(contest: C): C =>
  Array.isArray(contest.candidates)
    ? { ...contest, candidates: filterOnBallotCandidates(contest.candidates) }
    : contest;

/** One election with off-ballot contests, candidates and measures removed. */
export const filterOnBallotElection = <
  E extends {
    contests?: unknown;
    ballot_measures?: unknown;
    ballotMeasures?: unknown;
  },
>(
  election: E
): E => {
  const patch: {
    contests?: unknown[];
    ballot_measures?: unknown[];
    ballotMeasures?: unknown[];
  } = {};
  if (Array.isArray(election.contests)) {
    patch.contests = filterOnBallotContests(election.contests).map(withOnBallotCandidates);
  }
  const ballotMeasures = Array.isArray(election.ballot_measures)
    ? election.ballot_measures
    : Array.isArray(election.ballotMeasures)
      ? election.ballotMeasures
      : [];
  if (ballotMeasures.length) {
    const filteredMeasures = filterOnBallotMeasures(ballotMeasures);
    if (Array.isArray(election.ballot_measures)) patch.ballot_measures = filteredMeasures;
    if (Array.isArray(election.ballotMeasures)) patch.ballotMeasures = filteredMeasures;
  }
  return { ...election, ...patch };
};

/** National snapshot rows are just collections of elections, so the same roster
 * filtering applies before any summary counts or UI rendering read them. */
export const filterOnBallotNationalStates = <S extends { elections?: unknown }>(
  states: readonly S[] | null | undefined
): S[] =>
  (states ?? []).map((state) =>
    isRecord(state) && Array.isArray(state.elections)
      ? {
          ...state,
          elections: state.elections.map((election: unknown) =>
            isRecord(election) ? filterOnBallotElection(election) : election
          ),
        }
      : state
  );

/** A ballot payload — `ballot_user`, a ballot record — with every election
 *  filtered. Applied once where the payload enters the app, so rendering,
 *  counts, completeness and picks all read the same roster. */
const ballotCandidateIds = (elections: readonly unknown[]): Set<string> => {
  const ids = new Set<string>();
  for (const election of elections) {
    if (!isRecord(election)) continue;
    const contests = Array.isArray(election.contests) ? election.contests : [];
    for (const contest of contests) {
      if (!isRecord(contest) || !Array.isArray(contest.candidates)) continue;
      for (const candidate of contest.candidates) {
        if (isRecord(candidate) && typeof candidate.id === 'string') ids.add(candidate.id);
      }
    }
  }
  return ids;
};

const ballotMeasureIds = (elections: readonly unknown[]): Set<string> => {
  const ids = new Set<string>();
  for (const election of elections) {
    if (!isRecord(election)) continue;
    const measures = Array.isArray(election.ballot_measures)
      ? election.ballot_measures
      : Array.isArray(election.ballotMeasures)
        ? election.ballotMeasures
        : [];
    for (const measure of measures) {
      if (isRecord(measure) && typeof measure.id === 'string') ids.add(measure.id);
    }
  }
  return ids;
};

const ballotSelectionComplete = (
  elections: readonly unknown[],
  selectedIds: Set<string>
): boolean => {
  for (const election of elections) {
    if (!isRecord(election)) continue;
    const contests = Array.isArray(election.contests) ? election.contests : [];
    for (const contest of contests) {
      if (!isRecord(contest) || !Array.isArray(contest.candidates)) continue;
      const contestIds = contest.candidates
        .map((candidate) =>
          isRecord(candidate) && typeof candidate.id === 'string' ? candidate.id : ''
        )
        .filter((id) => id.length > 0);
      if (contestIds.length === 0) continue;
      if (!contestIds.some((id) => selectedIds.has(id))) return false;
    }
  }
  return true;
};

export const filterOnBallotElections = <
  B extends {
    elections?: unknown;
    selections?: unknown;
    is_complete?: boolean;
    manually_complete?: boolean;
  },
>(
  ballot: B
): B => {
  if (!Array.isArray(ballot.elections)) return ballot;

  const elections = ballot.elections.map((election: unknown) =>
    isRecord(election) ? filterOnBallotElection(election) : election
  );
  const candidateIds = ballotCandidateIds(elections);
  const measureIds = ballotMeasureIds(elections);
  const selections = isRecord(ballot.selections) ? ballot.selections : null;
  const filteredSelections = selections
    ? {
        ...selections,
        candidates: Array.isArray(selections.candidates)
          ? selections.candidates.filter(
              (candidateId) => typeof candidateId === 'string' && candidateIds.has(candidateId)
            )
          : [],
        measures: isRecord(selections.measures)
          ? Object.fromEntries(
              Object.entries(selections.measures).filter(([measureId]) => measureIds.has(measureId))
            )
          : {},
      }
    : selections;

  const selectedIds = new Set(
    Array.isArray(filteredSelections?.candidates)
      ? filteredSelections.candidates.filter(
          (candidateId): candidateId is string => typeof candidateId === 'string'
        )
      : []
  );
  const manuallyComplete = Boolean(ballot.manually_complete);
  const isComplete = manuallyComplete || ballotSelectionComplete(elections, selectedIds);

  return {
    ...ballot,
    elections,
    selections: filteredSelections,
    is_complete: isComplete,
    manually_complete: ballot.manually_complete,
  } as B;
};
